/**
 * CepagePastille — pastille de couleur ronde + libellé pour identifier
 * un cépage AOC Champagne (Chardonnay, Pinot Noir…).
 *
 * Source de couleur : champ `couleur` du référentiel `cepages` ('blanc',
 * 'noir', 'gris', 'rose'). Le mapping vers Tailwind est local — pas de
 * dépendance au design system pour rester explicite et tunable.
 *
 * Variante `inline` (par défaut) : pastille 10px + libellé. Variante `dot` :
 * juste la pastille (pour les tableaux compacts ou tooltips).
 */

import type { CouleurCepage } from "@champ-erp/contracts";
import type { CSSProperties } from "react";

// Mapping couleur cépage → couleur de pastille. Choix volontairement vifs et
// distincts pour qu'un vigneron en bottes voie la différence sans loupe.
const COULEUR_PASTILLE: Record<string, { bg: string; ring: string; label: string }> = {
  blanc: { bg: "#fef3c7", ring: "#d4af37", label: "blanc" },
  noir: { bg: "#5b3a4c", ring: "#3d2531", label: "noir" },
  gris: { bg: "#d6c4b8", ring: "#a68a72", label: "gris" },
  rose: { bg: "#f0bfb8", ring: "#c97a6b", label: "rosé" },
};

// Fallback pour cépage absent / référentiel incomplet (cas Voltis nouveau).
const PASTILLE_FALLBACK = { bg: "#e5e2dd", ring: "#9b948b", label: "" };

interface CepagePastilleProps {
  /** Libellé affiché à droite de la pastille. */
  libelle: string;
  /** Couleur du cépage telle que stockée en BDD (`blanc`, `noir`, …). */
  couleur: string | null;
  /**
   * `inline` (défaut) = pastille + libellé. `dot` = pastille seule.
   * `badge` = libellé avec pastille intégrée, fond doux.
   */
  variant?: "inline" | "dot" | "badge";
  /** Affiche un marqueur "conditionnel" (Voltis). */
  estConditionnel?: boolean;
  className?: string;
}

export function CepagePastille({
  libelle,
  couleur,
  variant = "inline",
  estConditionnel = false,
  className = "",
}: CepagePastilleProps) {
  // Couleur "" (chaîne vide) ne doit pas court-circuiter en fallback string ""
  // mais bien retomber sur PASTILLE_FALLBACK. On évite le chaining `&&` qui
  // retournerait "" tel quel — on passe par un check explicite.
  const palette = couleur ? (COULEUR_PASTILLE[couleur] ?? PASTILLE_FALLBACK) : PASTILLE_FALLBACK;
  const dotStyle: CSSProperties = {
    backgroundColor: palette.bg,
    borderColor: palette.ring,
  };

  if (variant === "dot") {
    return (
      <span
        aria-label={`Cépage ${libelle} (${palette.label})`}
        className={`inline-block h-2.5 w-2.5 rounded-full border ${className}`}
        style={dotStyle}
        title={libelle}
      />
    );
  }

  if (variant === "badge") {
    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full bg-champagne-50 px-2.5 py-1 text-[12px] font-medium text-champagne-900 ring-1 ring-champagne-200 ${className}`}
      >
        <span
          aria-hidden="true"
          className="inline-block h-2 w-2 rounded-full border"
          style={dotStyle}
        />
        {libelle}
        {estConditionnel && (
          <span
            className="ml-0.5 rounded bg-rust-100 px-1 py-0.5 text-[10px] uppercase tracking-wide text-rust-700"
            title="Cépage conditionnel : nécessite une convention INAO/ODG préalable"
          >
            cond.
          </span>
        )}
      </span>
    );
  }

  // variant === "inline"
  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <span
        aria-hidden="true"
        className="inline-block h-2.5 w-2.5 shrink-0 rounded-full border"
        style={dotStyle}
      />
      <span className="text-[13px] text-champagne-900">{libelle}</span>
      {estConditionnel && (
        <span
          className="rounded bg-rust-100 px-1 py-0.5 text-[10px] uppercase tracking-wide text-rust-700"
          title="Cépage conditionnel : nécessite une convention INAO/ODG préalable"
        >
          cond.
        </span>
      )}
    </span>
  );
}

/** Couleur du cépage (typing helper). */
export type { CouleurCepage };
